import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleX } from "@fortawesome/pro-solid-svg-icons";
import moment from "moment";

import { StatusCode } from "api/protocols";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";

import { GetAnnouncement, TLeagueAnnouncement } from "api/rpc/2024-04/facilityAdmin/league/announcement";
import { GetParticipant } from "api/rpc/2024-04/facilityAdmin/league/participant";
import { GetLeagueScoresheets } from "api/rpc/2024-04/facilityAdmin/league/scoring/scoresheet";

import { showError } from "redux/actions/ui";
import { ILeague, ILeagueParticipant, TLeagueScoresheet } from "redux/reducers/models/league";
import { IFacility } from "redux/reducers/models/facility";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";

import CopyLink from "components/copyLink/CopyLink";
import { Badge } from "components/badge/Badge";

import "./League.scss";
import { GetFacility } from "api/rpc/2024-04/facilityAdmin/facility/facility";
import Markdown from "components/markdown/Markdown";

interface ILeagueState {
  league: ILeague;
  facility: IFacility;
  announcements: TLeagueAnnouncement[];
  participants: ILeagueParticipant[];
  latestScoresheet: TLeagueScoresheet;
}

const ANNOUNCEMENT_LIMIT = 1;
const PARTICIPANT_LIMIT = 3;

export default function League() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const dispatch = useAppDispatch();

  const [state, setState] = useState<ILeagueState>({
    league: undefined,
    facility: undefined,
    announcements: undefined,
    participants: undefined,
    latestScoresheet: undefined,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadLeague(leagueId, source.token);
    return () => source.cancel();
  }, [leagueId]);

  async function loadLeague(leagueId: string | number, token?: CancelToken) {
    const leagueRes = await GetLeague({ id: leagueId }, true, token);

    if (token && token.reason) {
      return;
    }
    if (leagueRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.league.league.001")));
      return;
    }

    const league = leagueRes.data[0];

    setState(prevState => ({
      ...prevState,
      league,
    }));

    const facilityRes = await GetFacility({ id: league.facility_id }, true, token);

    if (token && token.reason) {
      return;
    }
    if (facilityRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.league.league.002")));
      return;
    }

    const facility = facilityRes.data?.[0];

    if (facilityRes.status === StatusCode.OK) {
      setState(prevState => ({
        ...prevState,
        facility,
      }));
    }

    // Load recent announcements
    const announcementRes = await GetAnnouncement(
      {
        league_id: league.id,
        limit: ANNOUNCEMENT_LIMIT,
      },
      true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (announcementRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league announcements")));
    } else {
      setState(prevState => ({
        ...prevState,
        announcements: announcementRes.data,
      }));
    }

    // Load recent registrations
    const participantRes = await GetParticipant(
      {
        league_id: league.id,
        limit: PARTICIPANT_LIMIT,
      },
      true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (participantRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league participants")));
    } else {
      setState(prevState => ({
        ...prevState,
        participants: participantRes.data,
      }));
    }

    // Load latest scoresheet
    const scoresheetRes = await GetLeagueScoresheets(
      {
        league_id: league.id,
        latest: true,
      },
      true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (scoresheetRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading latest scoresheet")));
    } else {
      setState(prevState => ({
        ...prevState,
        latestScoresheet: scoresheetRes.data[0],
      }));
    }
  }

  return state.league ? (
    <Page title={state.league.name} splitLayout
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Leagues"),
          url: "/admin/events",
        },
      ]}
    >
      <Page.Section twoThirds>
        <Card>
          <Card.Section
            title={t("Details")}
            sectionTitleActions={[
              {
                content: <FontAwesomeIcon icon={["far", "pencil"]} />,
                action: () => history.push(`/admin/league/${leagueId}/league-settings/general`),
              },
            ]}
          >
            <div className="flex justify-start gap-2">
              {moment(state.league?.start_date).format("MMM DD, YYYY")}
              <FontAwesomeIcon className="mt-1" icon={"arrow-right"} />
              {moment(state.league?.end_date).format("MMM DD, YYYY")}
            </div>
            <div className="flex justify-between">
              {`${state.league?.total_participant_count} Participants` /* TODO: Translation */}
              <div className="flex justify-start gap-2">
                {state.league?.capacity_remaining > 0 ? (
                  <Badge type="success">{
                    `${state.league?.capacity_remaining} Spots Left` /* TODO: Translation */
                  }</Badge>
                ) : (
                  <Badge type="error">{t("League is Full")}</Badge>
                )}
                {state.league?.capacity_remaining > 0 ? (
                  <Badge type="success">{
                    `${state.league?.total_paid_participant_count} Paid` /* TODO: Translation */
                  }</Badge>
                ) : (
                  <Badge type="gray">{t("0 Paid")}</Badge>
                )}
                {state.league?.total_participant_count - state.league?.total_paid_participant_count > 0 ? (
                  <Badge type="warning">{
                    `${
                      state.league?.total_participant_count - state.league?.total_paid_participant_count
                    } Unpaid` /* TODO: Translation */
                  }</Badge>
                ) : (
                  <Badge type="gray">{t("0 Unpaid")}</Badge>
                )}
              </div>
            </div>
            <div>
              {state?.league.display_online ? (
                <div className="flex justify-start gap-2">
                  <FontAwesomeIcon className="mt-1" icon={faCircleCheck} color={"#46B171"} />
                  {t("League is being displayed online")}
                </div>
              ) : (
                <div className="flex justify-start gap-2">
                  <FontAwesomeIcon className="mt-1" icon={faCircleX} color={"#AA0504"} />
                  {t("League is not being displayed online")}
                </div>
              )}
            </div>
          </Card.Section>
          <Card.Section
            title={t("Registration")}
            sectionTitleActions={[
              {
                content: <FontAwesomeIcon icon={["far", "pencil"]} />,
                action: () => history.push(`/admin/league/${leagueId}/league-settings/registration`),
              },
            ]}
          >
            {state.league.registration_open ? (
              <div>
                <div className="mb-2">{t("Registration is Open!")}</div>
                <CopyLink
                  label={t("Registration Link")}
                  link={window.origin + `/league/${state.facility?.short_name}/${state.league?.handle}/register`}
                />
              </div>
            ) : (
              <div>{t("Registration is Not Enabled")}</div>
            )}
          </Card.Section>
        </Card>
      </Page.Section>
      <Page.Section oneThird>
        <Card title={t("Latest Announcement")}>
          {state.announcements?.length > 0 ? (
            <div className="league-home-announcements">
              {state.announcements.map(announcement => (
                <Card.Section key={announcement.id}>
                  <div className="league-home-announcements-header">{announcement.subject}</div>
                  <div className="league-home-announcements-details">
                    {moment(announcement.created_at).format("MMM DD, YYYY")}
                  </div>
                  <Markdown markdownText={announcement.content} />
                </Card.Section>
              ))}
              <Card.Section>
                <div
                  className="flex justify-start gap-2"
                  onClick={() => history.push(`/admin/league/${leagueId}/announcements`)}
                >
                  {t("View All Announcements")}
                  <FontAwesomeIcon className="mt-1" icon={"arrow-right"} />
                </div>
              </Card.Section>
            </div>
          ) : (
            <Card.Section>
              <p>{t("No Announcements")}</p>
            </Card.Section>
          )}
        </Card>
        <Card title={t("Latest Registrations")}>
          {state.participants?.length > 0 ? (
            <>
              {state.participants.map(participant => (
                <Card.Section key={participant.id}>{participant.customer.full_name}</Card.Section>
              ))}
              <Card.Section>
                <div
                  className="flex justify-start gap-2"
                  onClick={() => history.push(`/admin/league/${leagueId}/players`)}
                >
                  {t("View All Players")}
                  <FontAwesomeIcon className="mt-1" icon={"arrow-right"} />
                </div>
              </Card.Section>
            </>
          ) : (
            <Card.Section>
              <p>{t("No Participants")}</p>
            </Card.Section>
          )}
        </Card>
      </Page.Section>
    </Page>
  ) : (
    <div />
  );
}
